import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Code, Group, Select, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { listLocations } from '../locations/api';
import {
  moveLicensePlate,
  unloadLicensePlate,
  type LicensePlateDto,
} from './api';

export type PlateAction = 'move' | 'unload';

interface Props {
  plate: LicensePlateDto | null;
  action: PlateAction;
  onClose: () => void;
  onDone: () => void;
}

/**
 * Moves a whole plate, or takes everything off it.
 *
 * The destination is REQUIRED for a move and optional for an unload, and that asymmetry is the
 * point: unloading a pallet where it stands is the ordinary case. The legacy system cannot do it —
 * sp_WebLicensePlate_Unload carries the move's "destination must differ from source" guard, which
 * is right for a move and wrong for an unload.
 */
export function MovePlateModal({ plate, action, onClose, onDone }: Props) {
  const { t } = useTranslation();
  const [toLocationId, setToLocationId] = useState<string>('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [correlationId, setCorrelationId] = useState<string | null>(null);

  const opened = plate !== null;

  // The plate's own warehouse — a plate can only move within the warehouse its stock sits in.
  const locations = useQuery({
    queryKey: ['locations', plate?.warehouseId ?? 'none'] as const,
    queryFn: ({ signal }) => listLocations({ warehouseId: plate!.warehouseId! }, signal),
    enabled: opened && Boolean(plate?.warehouseId),
  });

  useEffect(() => {
    if (opened) {
      setToLocationId('');
      setReason('');
      setError(null);
      setCorrelationId(null);
    }
  }, [opened]);

  const locationOptions = useMemo(
    () =>
      (locations.data ?? [])
        // Destination only, so inactive locations are left out: the server refuses stock moving
        // into one, and offering it would be an invitation to a refusal.
        .filter((l) => l.isActive && l.locationId !== plate?.locationId)
        .map((l) => ({
          value: l.locationId,
          label: l.displayName ? `${l.locationCode} — ${l.displayName}` : l.locationCode,
        })),
    [locations.data, plate?.locationId],
  );

  const mutation = useMutation({
    mutationFn: () =>
      action === 'move'
        ? moveLicensePlate(plate!.licensePlateId, toLocationId, reason.trim() || undefined)
        : unloadLicensePlate(plate!.licensePlateId, toLocationId || undefined, reason.trim() || undefined),
    onSuccess: () => {
      notifications.show({
        color: 'teal',
        title: action === 'move' ? t('licensePlates.movedTitle') : t('licensePlates.unloadedTitle'),
        message:
          action === 'move'
            ? t('licensePlates.movedBody', { number: plate!.licensePlateNumber })
            : t('licensePlates.unloadedBody', { number: plate!.licensePlateNumber }),
      });
      onDone();
      onClose();
    },
    onError: (e) => {
      // The server names what blocked it — the number of locations a split plate is in, or the
      // location that is short. Replacing that with a generic failure would send the operator in a
      // circle.
      setError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title ?? e.message) : String(e));
      if (e instanceof ApiError) setCorrelationId(e.problem.correlationId ?? null);
    },
  });

  const split = (plate?.locationCount ?? 0) > 1;
  const empty = (plate?.itemCount ?? 0) === 0;
  const canSubmit = !split && !empty && (action === 'unload' || toLocationId !== '');

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      width="md"
      title={
        <Group gap="xs" wrap="nowrap">
          <Text fw={600}>
            {action === 'move' ? t('licensePlates.moveTitle') : t('licensePlates.unloadTitle')}
          </Text>
          <Code>{plate?.licensePlateNumber ?? ''}</Code>
        </Group>
      }
    >
      <Stack gap="md">
        {/* Both blockers are visible before the button is pressed, rather than arriving as a
            refusal. */}
        {empty && (
          <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
            {t('licensePlates.nothingToMove')}
          </Alert>
        )}

        {split && (
          <Alert
            color="yellow"
            variant="light"
            icon={<IconAlertCircle size={16} />}
            title={t('licensePlates.splitTitle')}
          >
            {t('licensePlates.splitBlocksMove')}
          </Alert>
        )}

        {!empty && !split && (
          <>
            <Text size="sm" c="dimmed">
              {action === 'move'
                ? t('licensePlates.moveHint', {
                    items: plate?.itemCount ?? 0,
                    location: plate?.locationCode ?? '',
                  })
                : t('licensePlates.unloadHint', { location: plate?.locationCode ?? '' })}
            </Text>

            <Select
              label={t('licensePlates.destination')}
              // Optional for an unload: leaving it blank takes the stock off the plate where it
              // already is.
              description={
                action === 'unload' ? t('licensePlates.destinationOptional') : undefined
              }
              placeholder={
                action === 'unload' ? t('licensePlates.stayHere', { location: plate?.locationCode ?? '' }) : undefined
              }
              data={locationOptions}
              value={toLocationId || null}
              onChange={(v) => setToLocationId(v ?? '')}
              searchable
              clearable={action === 'unload'}
              nothingFoundMessage={t('shell.noMatch')}
              disabled={locations.isLoading}
            />

            <TextInput
              label={t('inventory.move.reason')}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              maxLength={200}
            />
          </>
        )}

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            <Stack gap={4}>
              <Text size="sm">{error}</Text>
              {correlationId && (
                <Text size="xs" c="dimmed">
                  {t('errors.genericBody')} <Code>{correlationId}</Code>
                </Text>
              )}
            </Stack>
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>
            {t('shell.cancel')}
          </Button>
          <Button
            color="invetrak"
            loading={mutation.isPending}
            disabled={!canSubmit}
            onClick={() => mutation.mutate()}
          >
            {action === 'move' ? t('licensePlates.moveSubmit') : t('licensePlates.unloadSubmit')}
          </Button>
        </Group>
      </Stack>
    </AppModal>
  );
}
